// Game event log for Letter Flick.
//
// `GameEvent` is the discriminated-union record of every game-affecting action
// the player has taken during the current session. The log is append-only for
// the life of a GameState — undo does NOT rewind the log; instead, undo
// appends a `{type:'undo'}` event after the snapshot-based state restore.
// The log can therefore contain "phantom" flick events that were subsequently
// undone away. The share-grid generator (sharegrid.ts) walks the log in order
// to snapshot the word-bar state at each undo (and at end-of-game).
//
// Phase 8 (schema v4): flick events carry the exited LETTER alongside
// slotIndex. The share grid renders multi-row attempt snapshots — one row
// per undo and one for the final state. Reconstructing those snapshots requires
// knowing which letter occupied which slot at each point in the log, which is
// what `letter` records. The bar in state.exitedLetters is the live mirror;
// the eventLog is the durable record for share-grid reconstruction across reloads.
//
// Per-slot share-grid glyph mapping lives in sharegrid.ts (shareGlyphForSlot);
// the SlotState classification it consumes lives in slotStates.ts (classifySlot).
// This file owns event shape only.

import type { FilledSlotState } from './slotStates'

export type GameEvent =
  | {
      readonly type: 'flick'
      readonly slotIndex: number
      // The lowercase letter that exited the board into this slot. Stored
      // verbatim so the share-grid renderer can compare it to targetWord[i]
      // without re-deriving anything from token state.
      readonly letter: string
      // Optional: present on all new flicks since Phase 9; absent on legacy
      // v4 persisted logs. sharegrid.ts falls back to letter-equality when
      // missing — that fallback collapses correct-canonical and correct-alt,
      // which is the pre-Phase-9 behavior.
      readonly slotState?: FilledSlotState
    }
  | { readonly type: 'undo' }

// Runtime narrowing guard. Used only by persistence.ts when re-hydrating a
// serialized log; in-memory log construction is purely typed. The flick branch
// REQUIRES the v4 `letter` field — pre-launch we accept no legacy shape (see
// persistence.ts migrateV3ToV4 for rationale). slotState is optional: present
// on new flicks, absent on persisted logs from before the field existed.
export function isGameEvent(v: unknown): v is GameEvent {
  if (v === null || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  const t = o['type']
  if (t === 'flick') {
    if (typeof o['slotIndex'] !== 'number' || !Number.isFinite(o['slotIndex'])) return false
    const letter = o['letter']
    if (typeof letter !== 'string' || letter.length !== 1) return false
    const ss = o['slotState']
    if (
      ss !== undefined &&
      ss !== 'correct-canonical' &&
      ss !== 'correct-alt' &&
      ss !== 'incorrect'
    ) {
      return false
    }
    return true
  }
  if (t === 'undo') {
    return true
  }
  return false
}

export function isGameEventArray(v: unknown): v is GameEvent[] {
  if (!Array.isArray(v)) return false
  for (const e of v) {
    if (!isGameEvent(e)) return false
  }
  return true
}
